import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Tractor, Coins, Globe2, Cpu, Leaf, MountainSnow, LineChart, Target, Sprout } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";

const ICONS = { Tractor, Coins, Globe2, Cpu, Leaf, MountainSnow, LineChart, Target, Sprout };

export default function Home() {
  const [home, setHome] = useState(null);
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    api.get("/homepage").then((r) => setHome(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/projects").then((r) => setPosts((r.data || []).slice(0, 3))).catch(() => {});
    api.get("/blog/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  if (!home) return <div className="h-screen flex items-center justify-center text-[#6B7280]">Caricamento…</div>;

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img src={home.hero_image} alt="Agricoltura Sardegna" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A14]/90 via-[#0F1A14]/55 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 md:grid-cols-12 gap-8 w-full">
          <div className="md:col-span-8 text-white anim-fade-up">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-[#A7E2C0] mb-6">
              {home.hero_overline}
            </span>
            <h1 className="font-display font-black tracking-tighter leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              {home.hero_title}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
              {home.hero_subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contatti" data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#007A3D] text-white rounded-full px-7 py-4 font-medium transition-colors shadow-2xl shadow-[#00A651]/30">
                {home.hero_cta_primary} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/consulenza-imprese-agricole-sardegna" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full px-7 py-4 font-medium border border-white/25 transition-colors">
                {home.hero_cta_secondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ARTICOLO IN EVIDENZA — punto cardine del sito */}
      {featured && (
        <section className="py-20 md:py-28 bg-white relative" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#007A3D]">Articolo in evidenza</span>
                <h2 className="mt-3 font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] leading-tight">Approfondimento del mese</h2>
              </div>
            </div>
            <Link to={featured.url || `/blog/${featured.slug}`} data-testid={`featured-card-${featured.slug}`}
              className="group block rounded-3xl overflow-hidden border-2 border-[#00A651]/20 bg-white hover-lift shadow-xl shadow-[#00A651]/10">
              <div className="grid grid-cols-1 md:grid-cols-12">
                <div className="md:col-span-6 relative aspect-[4/3] md:aspect-auto overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-[#00A651] text-white rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] shadow-lg shadow-[#00A651]/30">In evidenza</div>
                </div>
                <div className="md:col-span-6 p-8 md:p-12 flex flex-col justify-center">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#007A3D]">{featured.category?.replace(/-/g, " ")}</span>
                  <h3 className="mt-4 font-display font-black tracking-tighter text-2xl sm:text-3xl md:text-4xl text-[#1A1A1A] leading-tight">{featured.title}</h3>
                  <p className="mt-5 text-[#374151] leading-relaxed">{featured.excerpt}</p>
                  <span className="mt-8 inline-flex items-center gap-2 text-[#007A3D] group-hover:text-[#00A651] font-medium">
                    Leggi l’articolo <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#007A3D]">{home.intro_overline}</span>
            <h2 className="mt-4 font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-tight text-[#1A1A1A]">
              {home.intro_title}
            </h2>
            <p className="mt-6 text-[#374151] text-lg leading-relaxed">{home.intro_body}</p>
            <div className="mt-8 flex gap-3">
              <Link to="/chi-siamo" data-testid="intro-cta-about"
                className="inline-flex items-center gap-2 text-[#007A3D] hover:text-[#00A651] font-medium">
                Scopri Betelgez <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img src={home.intro_image} alt="Territorio sardo" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section className="py-24 md:py-32 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#007A3D]">Cosa facciamo</span>
              <h2 className="mt-4 font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] leading-tight">Servizi su misura per le imprese agricole</h2>
            </div>
            <Link to="/servizi" data-testid="services-view-all" className="text-[#007A3D] hover:text-[#00A651] font-medium inline-flex items-center gap-2">
              Vedi tutti i servizi <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.slice(0, 6).map((s) => {
              const Icon = ICONS[s.icon] || Sprout;
              return (
                <Link key={s.slug} to={`/servizi/${s.slug}`} data-testid={`service-card-${s.slug}`}
                  className="group hover-lift bg-white rounded-3xl p-7 border border-[#E5E7EB] flex flex-col">
                  <div className="w-12 h-12 rounded-2xl bg-[#EAF8F0] flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#007A3D]" />
                  </div>
                  <h3 className="font-display font-bold text-xl tracking-tight text-[#1A1A1A]">{s.name}</h3>
                  <p className="mt-3 text-[#6B7280] text-sm leading-relaxed flex-1">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#007A3D] group-hover:text-[#00A651]">
                    Approfondisci <ArrowUpRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PILLAR TEASER */}
      <section className="py-24 md:py-32 bg-[#007A3D] text-white relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-50" />
        <img src="/assets/logo-symbol.png" alt="" aria-hidden className="absolute -right-12 -bottom-12 w-[26rem] opacity-[0.07] pointer-events-none select-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#A7E2C0]">{home.pillar_overline}</span>
            <h2 className="mt-4 font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-tight">{home.pillar_title}</h2>
            <p className="mt-6 text-white/85 text-lg max-w-2xl leading-relaxed">{home.pillar_excerpt}</p>
            <Link to="/consulenza-imprese-agricole-sardegna" data-testid="pillar-cta"
              className="mt-10 inline-flex items-center gap-2 bg-white text-[#007A3D] hover:bg-[#EAF8F0] rounded-full px-7 py-4 font-semibold transition-colors">
              {home.pillar_cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-4 hidden md:flex items-end justify-end">
            <div className="text-[10rem] font-black font-display tracking-tighter text-white/10 leading-none">SRD</div>
          </div>
        </div>
      </section>

      {/* METODO */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#007A3D]">{home.method_overline}</span>
            <h2 className="mt-4 font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-tight text-[#1A1A1A]">{home.method_title}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4 relative">
            {(home.method_steps || []).map((step, i) => (
              <div key={i} data-testid={`method-step-${i}`} className="relative pt-8">
                <div className="text-xs font-bold tracking-[0.2em] text-[#00A651]">{step.n}</div>
                <h4 className="mt-2 font-display font-bold text-xl text-[#1A1A1A] tracking-tight">{step.title}</h4>
                <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">{step.desc}</p>
                {i < (home.method_steps.length - 1) && (
                  <div className="hidden md:block absolute top-9 -right-2 w-4 h-px bg-[#E5E7EB]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGETTI */}
      {posts.length > 0 && (
        <section className="py-24 md:py-32 bg-[#F9FAFB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-12 flex-wrap gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#007A3D]">Iniziative</span>
                <h2 className="mt-4 font-display font-black tracking-tighter text-3xl sm:text-4xl text-[#1A1A1A]">Progetti</h2>
              </div>
              <Link to="/progetti" data-testid="progetti-view-all" className="text-[#007A3D] hover:text-[#00A651] font-medium inline-flex items-center gap-2">
                Tutti i progetti <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {posts.map((p) => {
                const href = (p.link && p.link.trim()) ? p.link.trim() : `/progetti/${p.slug}`;
                const ext = /^https?:\/\//i.test(href);
                const cardInner = (
                  <>
                    {p.image ? (
                      <div className="aspect-[4/3] overflow-hidden bg-[#F9FAFB] flex items-center justify-center">
                        <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] bg-gradient-to-br from-[#007A3D] via-[#00A651] to-[#0F1A14] flex items-center justify-center p-6">
                        <span className="font-display font-black tracking-tighter text-white text-4xl md:text-5xl text-center leading-none">{p.title}</span>
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-display font-bold text-lg text-[#1A1A1A] tracking-tight leading-snug">{p.title}</h3>
                      {p.short_desc && <p className="mt-3 text-sm text-[#6B7280] leading-relaxed line-clamp-3">{p.short_desc}</p>}
                      <span className="mt-4 inline-flex items-center gap-1.5 text-[#007A3D] font-medium text-sm">
                        Scopri di più {ext ? <ArrowUpRight className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                      </span>
                    </div>
                  </>
                );
                return ext ? (
                  <a key={p.slug} href={href} target="_blank" rel="noopener noreferrer" data-testid={`progetto-card-${p.slug}`}
                    className="group bg-white rounded-3xl border border-[#E5E7EB] overflow-hidden hover-lift flex flex-col">
                    {cardInner}
                  </a>
                ) : (
                  <Link key={p.slug} to={href} data-testid={`progetto-card-${p.slug}`}
                    className="group bg-white rounded-3xl border border-[#E5E7EB] overflow-hidden hover-lift flex flex-col">
                    {cardInner}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-[#EAF8F0] relative overflow-hidden">
        <img src="/assets/logo-symbol.png" alt="" aria-hidden className="absolute -left-16 -top-10 w-[20rem] opacity-[0.08] pointer-events-none select-none" />
        <img src="/assets/logo-symbol.png" alt="" aria-hidden className="absolute -right-16 -bottom-10 w-[20rem] opacity-[0.08] pointer-events-none select-none" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#007A3D]">{home.final_cta_overline}</span>
          <h2 className="mt-4 font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-tight text-[#1A1A1A]">{home.final_cta_title}</h2>
          <p className="mt-6 text-lg text-[#374151]">{home.final_cta_subtitle}</p>
          <Link to="/contatti" data-testid="final-cta-button"
            className="mt-10 inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#007A3D] text-white rounded-full px-8 py-4 font-medium transition-colors shadow-2xl shadow-[#00A651]/30">
            {home.final_cta_button} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
